import React from "react"

export type EventStatus =
  | "happening_now"
  | "starting_soon"
  | "upcoming"
  | "ended_recently"
  | "past"

export interface EventDateStatusProps {
  startsAt?: string | null
  endsAt?: string | null
  /** Use short month format (e.g., "Jan" vs "January") */
  shortFormat?: boolean
  className?: string
}

/**
 * Check if two dates represent the same calendar day in UTC
 */
export const isSameUTCDate = (a: Date, b: Date): boolean =>
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate()

/**
 * Determine the status of an event based on its start and end dates
 */
export const getEventStatus = (
  startsAt: Date | null,
  endsAt: Date | null
): EventStatus => {
  if (!startsAt) return "upcoming"

  const now = new Date()
  const start = startsAt
  const end = endsAt || startsAt

  // Check if happening now
  if (now >= start && now <= end) {
    return "happening_now"
  }

  // Check if starting soon (within a week)
  const oneWeekFromNow = new Date()
  oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7)

  if (start > now && start <= oneWeekFromNow) {
    return "starting_soon"
  }

  // Check if ended recently (within last 7 days)
  if (now > end) {
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

    if (end >= oneWeekAgo) {
      return "ended_recently"
    }

    return "past"
  }

  return "upcoming"
}

/**
 * Format a date range for display, using UTC to avoid timezone issues
 */
export const formatDateRange = (
  startsAt: Date | null,
  endsAt: Date | null,
  options: { shortFormat?: boolean } = {}
): string => {
  if (!startsAt) return ""

  const { shortFormat = false } = options

  // Use UTC to display calendar dates as stored (avoid local timezone conversion)
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: shortFormat ? "short" : "long",
    day: "numeric",
    timeZone: "UTC",
  }

  const startStr = startsAt.toLocaleDateString("en-US", formatOptions)

  // Skip end date if same calendar day or no end date
  if (!endsAt || isSameUTCDate(startsAt, endsAt)) {
    return startStr
  }

  // If same month, show "Month Day - Day"
  if (startsAt.getUTCMonth() === endsAt.getUTCMonth()) {
    const endDay = endsAt.getUTCDate()
    return `${startStr} - ${endDay}`
  }

  // Different months, show full range
  const endStr = endsAt.toLocaleDateString("en-US", formatOptions)
  return `${startStr} - ${endStr}`
}

const statusConfig: Record<EventStatus, { label: string; className: string }> =
  {
    happening_now: {
      label: "Happening now",
      className:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    },
    starting_soon: {
      label: "Starting soon",
      className:
        "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200",
    },
    ended_recently: {
      label: "Just ended",
      className:
        "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    },
    upcoming: {
      label: "",
      className: "",
    },
    past: {
      label: "",
      className: "",
    },
  }

/**
 * Display event date range with status indicator
 */
export const EventDateStatus: React.FC<EventDateStatusProps> = ({
  startsAt,
  endsAt,
  shortFormat = false,
  className = "",
}) => {
  if (!startsAt) return null

  const startDate = new Date(startsAt)
  const endDate = endsAt ? new Date(endsAt) : null

  const status = getEventStatus(startDate, endDate)
  const config = statusConfig[status]
  const dateRange = formatDateRange(startDate, endDate, { shortFormat })

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      <span className="text-gray-600 dark:text-gray-400 text-sm">
        {dateRange}
      </span>
      {config.label && (
        <span
          className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium ${config.className}`}
        >
          {config.label}
        </span>
      )}
    </div>
  )
}

export default EventDateStatus
